'use client';

import { Toaster } from '@askable/ui/core/sonner';
import { GoogleTagManager } from '@next/third-parties/google';
import { Figtree } from 'next/font/google';
import { usePathname } from 'next/navigation';
import { AskableIconLoading } from '@askable/ui/core/askable-icon';
import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { EgressPrebootBeacon } from 'src/components/EgressPrebootBeacon';
import { ReactGrab } from 'src/components/ReactGrab';
import { isEgressRoute } from 'src/utils/egress';

import { Providers } from './providers';

import '@askable/ui/styles/globals.css';

const figtree = Figtree({
  subsets: ['latin'],
  display: 'swap',
  weight: ['400', '600', '700', '900'],
});

const LIGHT_ROUTE_PATTERNS = [
  /^\/unmod\//,
  /^\/survey\//,
  /^\/opt-out/,
  /^\/insights\/(?!request$)(?!collections$)[^/]+$/,
  /^\/insights\/collections\/[^/]+$/,
  /^\/insights\/collections\/[^/]+\/insights\/[^/]+$/,
];

const isLightRoute = (pathname: string) => LIGHT_ROUTE_PATTERNS.some(pattern => pattern.test(pathname));

const RootLayout = ({ children }: { children: ReactNode }) => {
  // HACK: prevent dark mode poking through during SSR when going to unmod routes
  // TODO: check with design if we really do want to lock the light/dark mode down here, or if we should
  // allow the UI to render in the users preference (like we do in clients)
  const pathname = usePathname();
  const [, i18n] = useTranslation();
  const [isRTL, setIsRTL] = useState(false);
  const theme = isLightRoute(pathname) ? 'light' : 'dark';
  const isEgress = isEgressRoute(pathname);

  useEffect(() => {
    setIsRTL(i18n.language === 'ar');
  }, [i18n.language]);

  return (
    <html lang="en" data-theme="base" className={`${theme} h-full`}>
      <head>
        <link rel="icon" href="/favicon.png" sizes="any" />
        {isEgress ? <EgressPrebootBeacon /> : null}
      </head>
      <body className={`h-full ${figtree.className}`} dir={isRTL ? 'rtl' : ''}>
        <Suspense fallback={<AskableIconLoading isFullScreen />}>
          <Providers>{children}</Providers>
        </Suspense>
        {!isEgress && <ReactGrab />}
        {!isEgress && (
          /** Hack: force card sort toasts to only show 1 at a time */
          <Toaster visibleToasts={pathname.startsWith('/unmod/') ? 1 : undefined} />
        )}
      </body>
      {!isEgress && <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_ID!} />}
    </html>
  );
};

export default RootLayout;
